/**
 * Settings ▸ Security — opt in to two-factor authentication.
 *
 * MFA is optional for SystemDox self-serve: no gate forces TOTP setup after
 * signup (that drove onboarding drop-off). This is the discoverable place to
 * turn it on. "Set up" routes to the shared /mfa-setup (TOTP) page; the live
 * status comes from the user's Cognito MFA preference.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';

type MfaStatus = 'loading' | 'enrolled' | 'not_enrolled';

export default function SecuritySettingsPage() {
  const navigate = useNavigate();
  const { getMFAPreference } = useAuthContext();
  const [status, setStatus] = useState<MfaStatus>('loading');

  useEffect(() => {
    let active = true;
    getMFAPreference()
      .then((pref) => {
        if (active) setStatus(pref.enabled ? 'enrolled' : 'not_enrolled');
      })
      .catch(() => {
        // Best-effort: if the preference can't be read, offer setup anyway.
        if (active) setStatus('not_enrolled');
      });
    return () => {
      active = false;
    };
  }, [getMFAPreference]);

  const enrolled = status === 'enrolled';

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Security</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Protect your account with two-factor authentication.
      </p>

      <div className="mt-6 rounded-lg border border-slate-200 p-5 dark:border-slate-800">
        <div className="flex items-start gap-4">
          <div
            className={
              enrolled
                ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400'
                : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
            }
          >
            {enrolled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Two-factor authentication (2FA)
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Require a one-time code from an authenticator app in addition to your password.
              Optional, but strongly recommended for keeping your documentation private.
            </p>

            <div className="mt-4">
              {status === 'loading' && (
                <span className="text-sm text-slate-500 dark:text-slate-400">Checking status…</span>
              )}

              {status === 'enrolled' && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                  <ShieldCheck className="h-4 w-4" />
                  Enabled
                </span>
              )}

              {status === 'not_enrolled' && (
                <button
                  type="button"
                  onClick={() => navigate('/mfa-setup')}
                  className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Set up two-factor authentication
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
